<template>
  <div class="side-menu-drawer-root" :class="forwardedClass" v-bind="forwardedAttrs">
    <aside v-if="!isMobile" class="side-menu-drawer" :class="{ 'side-menu-drawer--disabled': disabled }">
      <slot />
    </aside>

    <template v-else>
      <Teleport to="body">
        <transition name="drawer-backdrop">
          <div v-if="isOpen" class="side-menu-drawer__backdrop" @click="close" />
        </transition>
        <transition name="drawer-slide">
          <aside
            v-if="isOpen"
            class="hp-ui-boundary side-menu-drawer side-menu-drawer--mobile"
            :class="{ 'side-menu-drawer--disabled': disabled }"
          >
            <div class="side-menu-drawer__header">
              <slot name="mobile-header" />
              <button class="side-menu-drawer__close" @click="close">
                <CloseIcon />
              </button>
            </div>
            <div class="side-menu-drawer__body">
              <slot />
            </div>
          </aside>
        </transition>
      </Teleport>
    </template>
  </div>
</template>

<script setup lang="ts">
import { CloseIcon } from '@hprofits/ui';
import { computed, useAttrs, watch } from 'vue';
import { useRoute } from 'vue-router';

defineOptions({
  inheritAttrs: false,
});

type Props = {
  open?: boolean;
  disabled?: boolean;
  mobile?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  open: false,
  disabled: false,
  mobile: false,
});

const emit = defineEmits<{
  'update:open': [value: boolean];
}>();

const route = useRoute();
const attrs = useAttrs();

const isMobile = computed(() => props.mobile);
const isOpen = computed(() => props.open);
const forwardedClass = computed(() => attrs.class as string | undefined);
const forwardedAttrs = computed(() => {
  const { class: _class, ...rest } = attrs;
  return rest;
});

const close = (): void => {
  if (props.disabled) {
    return;
  }
  emit('update:open', false);
};

watch(
  () => route.fullPath,
  () => {
    if (isOpen.value) {
      close();
    }
  },
);
</script>

<style scoped lang="scss">
.side-menu-drawer-root {
  min-height: 0;
}

.side-menu-drawer {
  overflow-y: auto;
  min-height: 0;
  padding: var(--space-4);

  &--disabled {
    pointer-events: none;
    opacity: 0.6;
  }

  &--mobile {
    position: fixed;
    top: 0;
    left: 0;
    z-index: var(--z-index-modal);
    width: 280px;
    max-width: 85vw;
    height: 100vh;
    height: 100dvh;
    max-height: 100dvh;
    min-height: 0;
    padding: 0;
    background: var(--surface-bg);
    box-shadow: var(--shadow-4);
    display: flex;
    flex-direction: column;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-4) var(--space-4) var(--space-2);
    flex-shrink: 0;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--space-8);
    height: var(--space-8);
    padding: 0;
    color: var(--surface-fg-medium);
    background: none;
    border: none;
    border-radius: var(--border-radius-button);
    cursor: pointer;
    transition: background-color var(--duration-fast) ease;

    &:hover {
      background-color: var(--neutral-10);
    }

    svg {
      width: var(--space-5);
      height: var(--space-5);
    }
  }

  &__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: var(--space-2) var(--space-4) calc(var(--space-4) + env(safe-area-inset-bottom, 0px));
  }

  &__backdrop {
    position: fixed;
    inset: 0;
    z-index: var(--z-index-modal-backdrop);
    background: var(--surface-backdrop);
  }
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform var(--duration-slow) var(--ease-in-out);
}

.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(-100%);
}

.drawer-backdrop-enter-active,
.drawer-backdrop-leave-active {
  transition: opacity var(--duration-normal) ease;
}

.drawer-backdrop-enter-from,
.drawer-backdrop-leave-to {
  opacity: 0;
}
</style>
